/**
 * React Query Hook for fetching invoice details
 * GET /api/v1/invoices/{id}
 */

import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { InvoiceDetails, ApiErrorResponse } from '@/types';
import { apiClient } from '@/api/client';

export const INVOICE_DETAILS_QUERY_KEY = 'invoiceDetails';

interface UseInvoiceDetailsOptions extends Omit<UseQueryOptions<InvoiceDetails, ApiErrorResponse>, 'queryKey' | 'queryFn'> {
  invoiceId: number;
}

/**
 * Map flat API response to nested InvoiceDetails structure
 */
function mapApiToInvoiceDetails(api: any): InvoiceDetails {
  return {
    invoiceId: String(api.invoiceId),
    invoiceNumber: api.invoiceNumber,
    invoiceDate: api.invoiceDate,
    dueDate: api.dueDate,
    status: api.status,
    customer: {
      customerId: api.buyerId || 0,
      customerName: api.buyerName || api.companyName || 'N/A',
      email: api.buyerEmail || api.companyEmail,
      phone: api.buyerPhone || api.companyPhone,
      billingAddress: [api.billingAddress, api.billingCity, api.billingState, api.billingPincode]
        .filter(Boolean)
        .join(', ') || undefined,
      gstin: api.buyerGstin || api.companyGstin,
    },
    order: api.orderId
      ? {
          orderId: api.orderId,
          orderNumber: api.orderNumber || '',
          orderDate: api.invoiceDate,
        }
      : undefined,
    lineItems: (api.items || []).map((item: any) => ({
      lineItemId: item.id,
      description: item.description || item.productName || '',
      quantity: item.quantity || 0,
      unit: item.quantityUnit || 'unit',
      unitPrice: item.unitPrice || 0,
      discount: item.discountAmount || 0,
      discountPercentage: item.discountPercentage || 0,
      taxRate: api.gstRate || 0,
      taxAmount: item.totalTax || 0,
      lineTotal: item.lineTotal || (item.quantity * item.unitPrice) || 0,
      taxes: [],
    })),
    financialSummary: {
      subtotal: api.subtotalAmount || api.taxableAmount || (api.totalAmount - (api.totalTaxAmount || 0)),
      totalDiscount: api.totalDiscountAmount || 0,
      totalTax: api.totalTaxAmount || ((api.items || []).reduce((sum: number, i: any) => sum + (i.totalTax || 0), 0)),
      cgstAmount: api.totalCgst || 0,
      sgstAmount: api.totalSgst || 0,
      igstAmount: api.totalIgst || 0,
      cessAmount: 0,
      totalAmount: api.totalAmount || 0,
      amountPaid: api.paidAmount || 0,
      balanceDue: api.balanceAmount || (api.totalAmount - (api.paidAmount || 0)),
    },
    payments: [],
    notes: api.notes,
    createdBy: api.createdBy,
    createdAt: api.createdAt,
    lastModifiedBy: api.lastModifiedBy,
    lastModifiedAt: api.lastModifiedAt,
  };
}

/**
 * Fetch detailed invoice information including line items, payments, and financial summary
 * @param options Query options including invoiceId
 * @returns Query result with invoice details
 */
export const useInvoiceDetails = ({ invoiceId, ...options }: UseInvoiceDetailsOptions) => {
  return useQuery<InvoiceDetails, ApiErrorResponse>({
    queryKey: [INVOICE_DETAILS_QUERY_KEY, invoiceId],
    queryFn: async () => {
      const response = await apiClient.get<any>(
        `/api/v1/invoices/${invoiceId}`
      );
      return mapApiToInvoiceDetails(response.data);
    },
    enabled: !!invoiceId && invoiceId > 0,
    staleTime: 30000,
    gcTime: 300000,
    retry: 2,
    ...options,
  });
};

/**
 * Helper function to manually refetch invoice details
 */
export const refetchInvoiceDetails = (queryClient: any, invoiceId: number) => {
  return queryClient.invalidateQueries({
    queryKey: [INVOICE_DETAILS_QUERY_KEY, invoiceId]
  });
};

/**
 * Helper function to prefetch invoice details (useful for list hover)
 */
export const prefetchInvoiceDetails = async (
  queryClient: any,
  invoiceId: number
) => {
  await queryClient.prefetchQuery({
    queryKey: [INVOICE_DETAILS_QUERY_KEY, invoiceId],
    queryFn: async () => {
      const response = await apiClient.get<any>(
        `/api/v1/invoices/${invoiceId}`
      );
      return mapApiToInvoiceDetails(response.data);
    },
    staleTime: 30000,
  });
};
